import { Settings } from "@/components/ui/settings";
import Constants from "expo-constants";
import {
    getPushNotificationsEnabled,
    getSystemNotificationPermission,
    openSystemNotificationSettings,
    registerPushDevice,
    requestSystemNotificationPermission,
    setPushDeviceEnabled,
    setPushNotificationsEnabled,
} from "@/lib/pushNotifications";
import { showAlert } from "@/lib/showAlert";
import { THEME, type ColorScheme } from "@/lib/theme";
import { TEST_IDS } from "@/testIdDefinition";
import { useColorScheme } from "nativewind";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppState, Platform, Switch } from "react-native";

export default function NotificationsSettings() {
    const { t } = useTranslation();
    const { colorScheme } = useColorScheme();
    const scheme = (colorScheme ?? "light") as ColorScheme;
    const [preferenceEnabled, setPreferenceEnabled] = useState(true);
    const [systemAllowed, setSystemAllowed] = useState(false);
    const [systemBlocked, setSystemBlocked] = useState(false);
    const [ready, setReady] = useState(false);
    const pendingEnableRef = useRef(false);

    const refreshSystemPermission = useCallback(async () => {
        const permission = await getSystemNotificationPermission();
        setSystemAllowed(permission.allowed);
        setSystemBlocked(!permission.allowed && !permission.canAskAgain);
        return permission;
    }, []);

    const enablePush = useCallback(async () => {
        await setPushNotificationsEnabled(true);
        setPreferenceEnabled(true);
        await setPushDeviceEnabled(true);
        await registerPushDevice();
    }, []);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            const [value, permission] = await Promise.all([
                getPushNotificationsEnabled(),
                getSystemNotificationPermission(),
            ]);
            if (cancelled) {
                return;
            }
            setPreferenceEnabled(value);
            setSystemAllowed(permission.allowed);
            setSystemBlocked(!permission.allowed && !permission.canAskAgain);
            setReady(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const subscription = AppState.addEventListener("change", (state) => {
            if (state !== "active") {
                return;
            }
            void (async () => {
                const permission = await refreshSystemPermission();
                if (permission.allowed && pendingEnableRef.current) {
                    pendingEnableRef.current = false;
                    await enablePush();
                }
            })();
        });
        return () => subscription.remove();
    }, [enablePush, refreshSystemPermission]);

    const promptOpenSystemSettings = useCallback(() => {
        showAlert(
            t("settings.notifications.openSettingsTitle"),
            t("settings.notifications.openSettingsMessage"),
            [
                { text: t("ui.cancel"), variant: "outline" },
                {
                    text: t("settings.notifications.openSettings"),
                    testID: TEST_IDS[
                        TEST_IDS.SETTINGS_NOTIFICATIONS_OPEN_SETTINGS_BUTTON
                    ],
                    onPress: () => {
                        pendingEnableRef.current = true;
                        void openSystemNotificationSettings();
                    },
                },
            ],
        );
    }, [t]);

    const onValueChange = useCallback(
        (next: boolean) => {
            if (!next) {
                pendingEnableRef.current = false;
                setPreferenceEnabled(false);
                void (async () => {
                    await setPushNotificationsEnabled(false);
                    await setPushDeviceEnabled(false);
                })();
                return;
            }

            void (async () => {
                const result = await requestSystemNotificationPermission();
                if (result === "granted") {
                    setSystemAllowed(true);
                    setSystemBlocked(false);
                    await enablePush();
                    return;
                }

                await refreshSystemPermission();
                if (result === "blocked") {
                    promptOpenSystemSettings();
                }
            })();
        },
        [enablePush, promptOpenSystemSettings, refreshSystemPermission],
    );

    if (Platform.OS === "web" || !Constants.expoConfig?.extra?.pushEnabled) {
        return null;
    }

    return (
        <Settings
            title={t("settings.notifications.title")}
            footer={
                systemBlocked
                    ? t("settings.notifications.systemDisabled")
                    : undefined
            }
        >
            <Settings.Item label={t("settings.notifications.push")}>
                <Switch
                    value={preferenceEnabled && systemAllowed}
                    disabled={!ready}
                    onValueChange={onValueChange}
                    trackColor={{
                        false: THEME[scheme].border,
                        true: THEME[scheme].primary,
                    }}
                    thumbColor="#FFFFFF"
                />
            </Settings.Item>
        </Settings>
    );
}
